import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { RefreshCw, ArrowLeft, Phone, LogOut } from "lucide-react";
import { listBookings, updateBookingStatus, setToken } from "@/lib/api";

const STATUS = ["new", "confirmed", "completed", "cancelled"];
const statusColor = {
  new: "text-gold border-gold/40",
  confirmed: "text-emerald-400 border-emerald-500/40",
  completed: "text-[#8A8A8A] border-white/20",
  cancelled: "text-red-400 border-red-500/40",
};

export default function Admin() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("bookings");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { setBookings(await listBookings()); }
    catch (e) {
      if (e?.response?.status === 401) { setToken(null); navigate("/login"); return; }
      toast.error("Could not load bookings.");
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const logout = () => { setToken(null); toast.success("Signed out."); navigate("/login"); };

  const cycle = async (b) => {
    const next = STATUS[(STATUS.indexOf(b.status) + 1) % STATUS.length];
    try {
      const updated = await updateBookingStatus(b.id, next);
      setBookings((prev) => prev.map((x) => (x.id === b.id ? updated : x)));
      toast.success(`${b.ref} → ${next}`);
    } catch { toast.error("Update failed."); }
  };

  const shown = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = STATUS.reduce((a, s) => ({ ...a, [s]: bookings.filter((b) => b.status === s).length }), {});

  return (
    <div className="bg-ink min-h-screen" data-testid="admin-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-2xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-[#8A8A8A] hover:text-gold transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-serif text-2xl text-[#F5F5F0]">Operator Console</span>
          </Link>
          <div className="flex items-center gap-3">
          <button onClick={load} data-testid="refresh-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 hover:border-gold hover:text-gold transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="overline">Refresh</span>
          </button>
          <button onClick={logout} data-testid="logout-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 hover:border-gold hover:text-gold transition-colors">
            <LogOut className="w-4 h-4" />
            <span className="overline">Log Out</span>
          </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {STATUS.map((s) => (
            <button key={s} onClick={() => setFilter(filter === s ? "all" : s)}
              data-testid={`stat-${s}`}
              className={`text-left border p-6 transition-colors ${filter === s ? "border-gold bg-surface" : "border-white/10 hover:border-white/25"}`}>
              <p className="font-serif text-5xl text-[#F5F5F0]">{counts[s] || 0}</p>
              <p className="overline mt-2 text-[#8A8A8A]">{s}</p>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-[#8A8A8A]">Loading reservations…</p>
        ) : shown.length === 0 ? (
          <div className="border border-white/10 p-16 text-center">
            <p className="font-serif text-3xl mb-2">No reservations yet</p>
            <p className="text-[#8A8A8A]">New booking requests will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {shown.map((b, i) => (
              <motion.div key={b.id}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`booking-row-${i}`}
                className="border border-white/10 bg-surface p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                <div className="lg:col-span-3">
                  <p className="overline text-gold">{b.ref}</p>
                  <p className="font-serif text-2xl mt-1">{b.name}</p>
                  <a href={`tel:${b.phone}`} className="text-sm text-[#8A8A8A] hover:text-gold flex items-center gap-2 mt-1">
                    <Phone className="w-3 h-3" /> {b.phone}
                  </a>
                </div>
                <div className="lg:col-span-5 text-sm">
                  <p className="text-[#c9c9c4]"><span className="text-gold">From</span> · {b.pickup_address}</p>
                  <p className="text-[#c9c9c4] mt-1"><span className="text-gold">To</span> · {b.dropoff_address}</p>
                  <p className="text-[#8A8A8A] mt-2">{b.vehicle} · {b.service_type} · {b.passengers} pax</p>
                  {b.notes ? <p className="text-[#8A8A8A] mt-1 italic">“{b.notes}”</p> : null}
                </div>
                <div className="lg:col-span-2 text-sm">
                  <p className="text-[#F5F5F0]">{b.pickup_datetime?.replace("T", " ")}</p>
                  {b.estimated_price ? (
                    <p className="font-serif text-2xl text-gold mt-1">${b.estimated_price}</p>
                  ) : <p className="text-[#8A8A8A] mt-1">est. n/a</p>}
                  {b.distance_miles ? <p className="text-[#8A8A8A]">{b.distance_miles} mi</p> : null}
                </div>
                <div className="lg:col-span-2 flex lg:justify-end">
                  <button onClick={() => cycle(b)} data-testid={`status-btn-${i}`}
                    className={`border px-4 py-2 overline ${statusColor[b.status]} hover:bg-white/5 transition-colors`}>
                    {b.status}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
